import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext, useWatch } from "react-hook-form";
import ResourceSelect from "../../../../../../../../components/shared/inputs/ResourceSelect";
import Select from "../../../../../../../../components/shared/inputs/Select";
import { useScheduleContext } from "../../../../../hooks/context/useScheduleContext";
import { useDateFilters } from "../../../../../../../../components/shared/filters/DateFilters/context/useDateFilters";
import { getCommonProperty } from "../../../../../helpers/form/getCommonProperty";
import { getSettingsResourceSearchFiltersWithLookups, renderSettingsResourceLabel } from "../../../../../../../../utils/helpers/settings";
import { unassignedShiftsResource } from "../../../../../helpers/data/unassignedShiftsResource";
import { useFilterResourceParams } from "../../../../../hooks/filters/useFilterResourceParams";
import { useUser } from "../../../../../../../../utils/hooks/user";
import { cn } from "../../../../../../../../lib/utils";

type LocationSelectProps = {
    value?: any;
    onChange?: (value: any) => void;
    disabled?: boolean;
    className?: string;
};

function LocationSelect({ value, onChange, disabled, className }: LocationSelectProps) {
    const { t } = useTranslation();
    const { selectedCellOrEvent, type } = useScheduleContext();
    const { mode } = useDateFilters();
    const { control } = useFormContext();
    const users = useWatch({ control, name: "users" });
    const loggedUser = useUser();
    const filterParams = useFilterResourceParams("location");

    const locations = useMemo(() => {
        const user = typeof selectedCellOrEvent?.user === "string"
            ? (selectedCellOrEvent as any)?.resource
            : selectedCellOrEvent?.user ?? (type === "home" ? loggedUser : undefined);
        if (mode === "month") {
            return getCommonProperty(
                users ?? [],
                "locations",
                "locationId",
                ["id", "code", "description", "timezoneId", "timezoneValue", "locationId"]
            );
        }
        return user ? getCommonProperty(
            user,
            "locations",
            "locationId",
            ["id", "code", "description", "timezoneId", "timezoneValue", "locationId"]
        ) : [];
    }, [selectedCellOrEvent, mode, users, type, loggedUser]);

    const isUnassigned = selectedCellOrEvent?.isUnassignedShift ||
        selectedCellOrEvent?.resourceId === unassignedShiftsResource.id;

    const onSelectChange = useCallback((code?: string) => {
        onChange?.(locations?.find((item: any) => item.code === code));
    }, [locations, onChange]);

    return isUnassigned ? (
        <ResourceSelect
            mode="single"
            resourcePath="/locations"
            renderLabel={renderSettingsResourceLabel}
            getSearchFilter={getSettingsResourceSearchFiltersWithLookups}
            placeholder={t("location")}
            disabled={disabled}
            value={value}
            onChange={onChange}
            className={className}
            params={{
                withoutLookups: true,
                ...(filterParams || {})
            }}
        />
    ) : (
        <Select
            className={cn("tw-w-full", className)}
            placeholder={t("location")}
            disabled={disabled}
            value={value?.code}
            onChange={onSelectChange}
        >
            {locations?.map((location: any) => (
                <Select.Item key={location.code} value={location.code}>
                    {renderSettingsResourceLabel(location)}
                </Select.Item>
            ))}
        </Select>
    );
}

export default LocationSelect;
